import React from 'react';
import { createRoot } from 'react-dom/client';
import Modal from 'react-modal';
import { X, CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';
import SEButton from './seButton';

Modal.setAppElement('#root');

const iconMap = {
    success: { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-100', },
    error: { icon: XCircle, color: 'text-red-600', bgColor: 'bg-red-100', },
    warning: { icon: AlertTriangle, color: 'text-yellow-500', bgColor: 'bg-yellow-100', },
    info: { icon: Info, color: 'text-blue-500', bgColor: 'bg-blue-100', },
};

const SEModalComponent = ({
    parentSelector,
    isOpen,
    onRequestClose,
    title,
    description,
    icon, type,
    children, html, text,
    onConfirm,
    confirmButtonText = 'Confirm',
    confirmButtonClassName = '',
    showConfirmButton = false,
    cancelButtonText = 'Cancel',
    cancelButtonClassName = '',
    showCancelButton = false,
    isWide = false,
    isWider = false,
    isWidest = false,
    timer = null,
}) => {
    const mainIcon = ((type || icon) && (isWide === false)) ? (iconMap[(type || icon)] || null) : null;
    const classWide = isWide ? (isWidest ? 'max-w-5xl' : (isWider ? 'max-w-3xl' : 'max-w-2xl')) : 'max-w-lg';

    React.useEffect(() => {
        if (!timer || !isOpen) return;
        const id = setTimeout(() => { onRequestClose(); }, timer);
        return () => clearTimeout(id);
    }, [timer, isOpen, onRequestClose]);

    return (
        <Modal
            parentSelector={parentSelector}
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            className={`focus:outline-none w-full ${classWide} mx-auto`}
            overlayClassName="fixed inset-0 bg-slate-950/80 z-50 transition-opacity duration-300 ease-in-out flex items-center justify-center p-4"
            contentLabel="SEModal"
            shouldCloseOnOverlayClick={true}
            style={{
                content: {
                    animation: 'fadeIn 0.3s ease-out forwards',
                },
                overlay: {
                    opacity: isOpen ? 1 : 0,
                }
            }}
        >
            <div className={`bg-white rounded-lg shadow-xl transform transition-all duration-300 ease-in-out scale-95 data-[state=open]:scale-100 p-6`}>
                {/* header */}
                <div className="flex justify-between items-start">
                    {isWide && <h3 className="text-xl font-semibold text-slate-800">{title}</h3>}
                    <button onClick={onRequestClose} className={`text-slate-400 hover:text-slate-600 ${isWide ? '' : 'ml-auto'}`}>
                        <X className="w-6 h-6" />
                    </button>
                </div>
                {/* content */}
                <div className={`mt-4 ${isWide ? 'text-left' : 'text-center'}`}>
                    {mainIcon &&
                        <div className={`mb-4 mx-auto flex items-center justify-center w-16 h-16 rounded-full ${mainIcon.bgColor} iconAnimBg`}>
                            <mainIcon.icon className={`w-8 h-8 ${mainIcon.color} iconAnim`} />
                        </div>}
                    {(title && !isWide) && <h3 className="text-xl font-semibold text-slate-800 mb-2">{title}</h3>}
                    {(description || text) && <p className="text-sm text-slate-600 mb-4">{(description || text)}</p>}
                    {children || html}
                </div>
                {/* Buttons */}
                <div className={`mt-6 w-full flex ${isWide ? 'flex-col md:flex-row md:justify-end' : 'flex-row justify-between'} items-center gap-3`}>
                    {showCancelButton && (
                        <button onClick={onRequestClose} className={`px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-md hover:bg-slate-200 ${!isWide ? 'flex-1' : ''} cursor-pointer`}>
                            {cancelButtonText}
                        </button>)}
                    {showConfirmButton && ((mainIcon)
                        ? <button onClick={onConfirm} className={`px-4 py-2 text-sm font-medium ${confirmButtonClassName} ${mainIcon.bgColor} rounded-md hover:${mainIcon.bgColor.replace('100', '200')} flex-1 cursor-pointer`}>
                            {confirmButtonText}
                        </button>
                        : <SEButton onClick={onConfirm} className={`px-4 py-1 text-sm font-medium `}>
                            {confirmButtonText}
                        </SEButton>
                    )}
                </div>
            </div>
            <style>
                {`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: scale(0.15); }
                        to { opacity: 1; transform: scale(1); }
                    }

                    @keyframes iconAnimBg {
                        0%   { transform: scale(0); opacity: 0; }
                        100% { transform: scale(1); opacity: 1; }
                    }
                    .iconAnimBg {
                        opacity: 0;
                        transform: scale(0);
                        animation-fill-mode: both;
                        animation: iconAnimBg 0.4s ease-out both;
                        animation-delay: 0s;
                    }

                    @keyframes iconAnim {
                        0%   { transform: scale(0); opacity: 0; }
                        60%  { transform: scale(1.2); opacity: 1; }
                        100% { transform: scale(1); opacity: 1; }
                    }

                    .iconAnim {
                        opacity: 0;
                        transform: scale(0);
                        animation: iconAnim 0.4s cubic-bezier(0.34,1.56,0.64,1) both;
                        animation-delay: 0.4s;
                    }
                `}
            </style>
        </Modal>
    );
};

const SEModal = {};

SEModal.fire = (options) => {
    return new Promise((resolve) => {
        const { title, description, html, text, type, icon, child, timer, ...rest } = options;

        const modalContainer = document.createElement('div');
        document.body.appendChild(modalContainer);
        const root = createRoot(modalContainer);

        const closeModal = (result) => {
            // 1- Collect id'd elements inside modal
            const contentEl = document.querySelector('.ReactModal__Content');
            const idElements = {};
            if (contentEl) {
                contentEl.querySelectorAll('[id]').forEach(el => {
                    switch (el.type) {
                        case 'checkbox':
                        case 'radio':
                            idElements[el.id] = el.checked;
                            break;
                        case 'text':
                        case 'email':
                        case 'password':
                        case 'number':
                            idElements[el.id] = el.value;
                            break;
                        default:
                            idElements[el.id] = el.innerText || el.textContent || '';
                            break;
                    }
                });
            }

            // 2- Remove modal from DOM
            root.unmount();
            modalContainer.remove();
            resolve({ ...result, idElements });
        };

        const handleConfirm = () => closeModal({ isConfirmed: true });

        const handleCancel = () => closeModal({ isConfirmed: false, isDismissed: true });

        root.render(
            <SEModalComponent
                parentSelector={() => modalContainer}
                isOpen={true}
                onRequestClose={handleCancel}
                title={title}
                description={description}
                text={text}
                html={html}
                type={type}
                icon={icon}
                onConfirm={handleConfirm}
                timer={timer}
                {...rest}
            >
                {child}
            </SEModalComponent>
        );
    });
};

export default SEModal;